/**
 * Legal Document Loader
 * 
 * Loads and parses legal documents from /legal folder
 * Simple text format with tags like TITLE:, SECTION:, PARA:, BULLET:, STRONG:, INTRO:
 */

const parseLegalDocument = (text) => {
  const lines = text.split('\n');
  const parsed = {
    title: '',
    lastUpdated: null,
    intro: null,
    sections: []
  };

  let currentSection = null;
  let currentParagraph = [];

  const ensureSection = () => {
    // Auto-create a default section if content needs one
    if (!currentSection) {
      currentSection = {
        title: '', // Empty title for default section
        content: []
      };
    }
  };

  const finishParagraph = () => {
    if (currentParagraph.length > 0 && currentSection) {
      currentSection.content.push({
        type: 'paragraph',
        text: currentParagraph.join(' ')
      });
      currentParagraph = [];
    }
  };

  const finishSection = () => {
    finishParagraph();
    if (currentSection) {
      parsed.sections.push(currentSection);
      currentSection = null;
    }
  };

  for (let line of lines) {
    const trimmed = line.trim();
    
    if (!trimmed) {
      finishParagraph();
      continue;
    }

    if (trimmed.startsWith('TITLE:')) {
      parsed.title = trimmed.substring(6).trim();
    } else if (trimmed.startsWith('Last Updated:')) {
      // Parse the "Last Updated:" line
      parsed.lastUpdated = trimmed.substring(13).trim();
    } else if (trimmed.startsWith('INTRO:')) {
      // Intro paragraph (for privacy policy)
      currentParagraph = [];
    } else if (trimmed.startsWith('SECTION:')) {
      finishSection();
      currentSection = {
        title: trimmed.substring(8).trim(),
        content: []
      };
    } else if (trimmed.startsWith('BULLET:')) {
      finishParagraph();
      ensureSection(); // Auto-create section if needed
      currentSection.content.push({
        type: 'bullet',
        text: trimmed.substring(7).trim()
      });
    } else if (trimmed.startsWith('PARA:')) {
      finishParagraph();
      ensureSection(); // Auto-create section if needed
      // Start a new paragraph from the text after PARA:
      const text = trimmed.substring(5).trim();
      if (text) {
        currentParagraph.push(text);
      }
    } else if (trimmed.startsWith('STRONG:')) {
      finishParagraph();
      ensureSection(); // Auto-create section if needed
      currentSection.content.push({
        type: 'strong',
        text: trimmed.substring(7).trim()
      });
    } else {
      // Regular text line - add to current paragraph
      if (parsed.sections.length === 0 && !currentSection) {
        // This is intro text
        if (!parsed.intro) parsed.intro = [];
        parsed.intro.push(trimmed);
      } else {
        currentParagraph.push(trimmed);
      }
    }
  }

  // Finish any remaining section
  finishSection();

  // Convert intro array to string if exists
  if (parsed.intro) {
    parsed.intro = parsed.intro.join(' ');
  }

  return parsed;
};

const loadLegalDocument = async (documentName) => {
  try {
    const response = await fetch(`${process.env.PUBLIC_URL}/legal/${documentName}.txt`);
    if (!response.ok) {
      throw new Error(`Failed to load ${documentName}`);
    }
    const text = await response.text();
    return parseLegalDocument(text);
  } catch (error) {
    console.error(`Error loading legal document ${documentName}:`, error);
    return null;
  }
};

export const loadTermsOfService = () => loadLegalDocument('terms-of-service');
export const loadPrivacyPolicy = () => loadLegalDocument('privacy-policy');
export const loadRiskDisclaimer = () => loadLegalDocument('risk-disclaimer');

export default {
  loadTermsOfService,
  loadPrivacyPolicy,
  loadRiskDisclaimer
};

